import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '../components/ui/tabs';
import { ClipboardList, Plus, Pencil, Trash2, Save, X, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const CampaignsAdminPage = () => {
  const { user } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('luz_gas');

  // Per-company local edit state: { "category|company": { campaigns: [...], dirty: bool, saving: bool, newValue: '', editingIdx: null, editingValue: '' } }
  const [edits, setEdits] = useState({});

  const allowed = user?.role === 'super_admin' || user?.role === 'gestor';

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/campaigns`);
      setData(res.data);
      // Initialize edit state
      const init = {};
      for (const cat of res.data) {
        for (const comp of cat.companies) {
          init[`${cat.category}|${comp.key}`] = {
            campaigns: [...comp.campaigns],
            dirty: false,
            saving: false,
            newValue: '',
            editingIdx: null,
            editingValue: '',
          };
        }
      }
      setEdits(init);
    } catch (err) {
      toast.error('Erro ao carregar campanhas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!allowed) {
    return (
      <Layout>
        <div className="p-6">
          <p className="text-red-600">Sem permissão para aceder a esta página.</p>
        </div>
      </Layout>
    );
  }

  const updateState = (key, patch) => {
    setEdits(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };

  const addCampaign = (category, company) => {
    const key = `${category}|${company}`;
    const state = edits[key];
    const value = (state?.newValue || '').trim();
    if (!value) return;
    if (state.campaigns.includes(value)) {
      toast.error('Esta campanha já existe');
      return;
    }
    updateState(key, {
      campaigns: [...state.campaigns, value],
      newValue: '',
      dirty: true,
    });
  };

  const removeCampaign = (category, company, idx) => {
    const key = `${category}|${company}`;
    const state = edits[key];
    if (!window.confirm(`Eliminar a campanha "${state.campaigns[idx]}"?`)) return;
    updateState(key, {
      campaigns: state.campaigns.filter((_, i) => i !== idx),
      editingIdx: null,
      dirty: true,
    });
  };

  const startEdit = (category, company, idx) => {
    const key = `${category}|${company}`;
    updateState(key, {
      editingIdx: idx,
      editingValue: edits[key].campaigns[idx],
    });
  };

  const cancelEdit = (category, company) => {
    const key = `${category}|${company}`;
    updateState(key, { editingIdx: null, editingValue: '' });
  };

  const confirmEdit = (category, company) => {
    const key = `${category}|${company}`;
    const state = edits[key];
    const value = (state.editingValue || '').trim();
    if (!value) {
      toast.error('Nome inválido');
      return;
    }
    const newCampaigns = state.campaigns.map((c, i) => (i === state.editingIdx ? value : c));
    // Check duplicates
    if (newCampaigns.filter((c, i) => c === value && i !== state.editingIdx).length > 0) {
      toast.error('Já existe uma campanha com este nome');
      return;
    }
    updateState(key, {
      campaigns: newCampaigns,
      editingIdx: null,
      editingValue: '',
      dirty: true,
    });
  };

  const save = async (category, company, companyName) => {
    const key = `${category}|${company}`;
    const state = edits[key];
    updateState(key, { saving: true });
    try {
      await axios.put(`${API_URL}/admin/campaigns/${category}/${company}`, {
        campaigns: state.campaigns,
      });
      toast.success(`Campanhas de ${companyName} guardadas`);
      updateState(key, { saving: false, dirty: false });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao guardar');
      updateState(key, { saving: false });
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 max-w-6xl" data-testid="campaigns-admin-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-outfit flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-[#2D3E6F]" />
            Gestão de Campanhas
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Adicione, edite ou remova campanhas por empresa. As alterações ficam imediatamente disponíveis no formulário de criação de contratos.
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-slate-200 p-1 h-auto rounded-full flex-wrap" data-testid="campaigns-category-tabs">
            {data.map(cat => (
              <TabsTrigger
                key={cat.category}
                value={cat.category}
                className="rounded-full data-[state=active]:bg-[#2D3E6F] data-[state=active]:text-white px-4 py-2 text-sm"
                data-testid={`tab-${cat.category}`}
              >
                {cat.category_label}
              </TabsTrigger>
            ))}
          </TabsList>

          {data.map(cat => (
            <TabsContent key={cat.category} value={cat.category} className="mt-6 space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {cat.companies.map(comp => {
                  const key = `${cat.category}|${comp.key}`;
                  const state = edits[key] || { campaigns: [], dirty: false, saving: false, newValue: '', editingIdx: null, editingValue: '' };
                  return (
                    <Card key={key} className="rounded-2xl border-slate-100" data-testid={`company-card-${comp.key}`}>
                      <CardHeader className="border-b border-slate-100 flex flex-row items-center justify-between">
                        <CardTitle className="text-base font-semibold flex items-center gap-2">
                          {comp.name}
                          <span className="text-xs font-normal text-slate-400">
                            ({state.campaigns.length} campanha{state.campaigns.length === 1 ? '' : 's'})
                          </span>
                        </CardTitle>
                        <Button
                          size="sm"
                          onClick={() => save(cat.category, comp.key, comp.name)}
                          disabled={!state.dirty || state.saving}
                          className="bg-[#2D3E6F] hover:bg-[#1a2a4a] rounded-full gap-1.5 h-8"
                          data-testid={`save-${comp.key}`}
                        >
                          {state.saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                          Guardar
                        </Button>
                      </CardHeader>
                      <CardContent className="p-4 space-y-2">
                        {state.campaigns.length === 0 ? (
                          <p className="text-sm text-slate-400 py-2">Sem campanhas. Adicione abaixo.</p>
                        ) : (
                          <div className="space-y-1.5">
                            {state.campaigns.map((c, idx) => (
                              <div key={idx} className="flex items-center gap-2 px-3 py-2 bg-slate-50 hover:bg-slate-100 rounded-lg group" data-testid={`campaign-row-${comp.key}-${idx}`}>
                                {state.editingIdx === idx ? (
                                  <>
                                    <Input
                                      value={state.editingValue}
                                      onChange={(e) => updateState(key, { editingValue: e.target.value })}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') { e.preventDefault(); confirmEdit(cat.category, comp.key); }
                                        if (e.key === 'Escape') { e.preventDefault(); cancelEdit(cat.category, comp.key); }
                                      }}
                                      className="h-8 text-sm"
                                      autoFocus
                                      data-testid={`edit-input-${comp.key}-${idx}`}
                                    />
                                    <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-600" onClick={() => confirmEdit(cat.category, comp.key)} data-testid={`confirm-edit-${comp.key}-${idx}`}>
                                      <Check className="w-4 h-4" />
                                    </Button>
                                    <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-500" onClick={() => cancelEdit(cat.category, comp.key)}>
                                      <X className="w-4 h-4" />
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <span className="flex-1 text-sm text-slate-700">{c}</span>
                                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                      <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-500 hover:text-[#2D3E6F]" onClick={() => startEdit(cat.category, comp.key, idx)} data-testid={`edit-${comp.key}-${idx}`}>
                                        <Pencil className="w-3.5 h-3.5" />
                                      </Button>
                                      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500 hover:text-red-700" onClick={() => removeCampaign(cat.category, comp.key, idx)} data-testid={`delete-${comp.key}-${idx}`}>
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </Button>
                                    </div>
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="flex items-center gap-2 pt-2 border-t border-slate-100 mt-2">
                          <Input
                            value={state.newValue}
                            onChange={(e) => updateState(key, { newValue: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') { e.preventDefault(); addCampaign(cat.category, comp.key); }
                            }}
                            placeholder="Nova campanha..."
                            className="h-9 text-sm"
                            data-testid={`new-campaign-input-${comp.key}`}
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-full gap-1.5 h-9"
                            onClick={() => addCampaign(cat.category, comp.key)}
                            disabled={!state.newValue.trim()}
                            data-testid={`add-${comp.key}`}
                          >
                            <Plus className="w-3.5 h-3.5" />
                            Adicionar
                          </Button>
                        </div>
                        {state.dirty && (
                          <p className="text-xs text-amber-600 flex items-center gap-1 pt-1">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />
                            Alterações não guardadas
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </Layout>
  );
};

export default CampaignsAdminPage;
